import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Modal,
  TextField,
  Typography,
  Alert,
  AlertTitle,
  Grid,
  Tooltip,
  IconButton,
  Stack,
  Chip,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import * as Constants from "../../support/Constants";
import * as Web3Manager from "../../support/Web3Manager";
import {
  CheckCircle,
  CloudDownload,
  DeleteOutline,
  Verified,
} from "@mui/icons-material";
import { CancelButton } from "../ui/CustomButtons";
import { useFormik } from "formik";
import SpinnerContext from "../../context/spinner/SpinnerContext";
import SociosContext from "../../context/socios/SociosContext";
import { UploadDocumentsModal } from "../UploadDocumentsModal/UploadDocumentsModal";
import Link from "@mui/material/Link";
import { useDocuments } from "../../hooks/useDocuments";
import { copyHash, shortHash } from "../utiles/hashUtils";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50%",
  maxHeight: "70%",
  overflow: "auto",
  bgcolor: "background.paper",
  border: "1px solid #000",
  boxShadow: 24,
  p: 4,
};

const ModalAdherirCompromiso = ({
  open,
  handleClose,
  compromiso,
  compromisoEditing,
}) => {
  const [disableButton, setDisableButton] = useState(false);

  const [documents, setDocuments] = useState([]);

  const { getFilesBatch } = useDocuments();
  const { openSpinner, closeSpinner } = useContext(SpinnerContext);
  const {
    adherirACompromiso,
    editarAdhesionCompromiso,
    eliminarAdhesionCompromiso,
    isLoading,
  } = useContext(SociosContext);

  const isEditing = compromisoEditing !== undefined;
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    const loadExistingDocuments = async () => {
      try {
        if (!isEditing || !compromisoEditing?.documents?.length) {
          setDocuments([]);
          return;
        }

        const docsFromBackend = compromisoEditing.documents;
        const fileIds = docsFromBackend
          .map((d) => Number(d.fileId ?? d.id))
          .filter((id) => !Number.isNaN(id));

        if (!fileIds.length) {
          setDocuments(docsFromBackend);
          return;
        }

        const files = await getFilesBatch(fileIds);

        const fileMap = files.reduce((acc, f) => {
          acc[f.id] = f;
          return acc;
        }, {});

        const merged = docsFromBackend.map((doc) => {
          const fileId = Number(doc.fileId ?? doc.id);
          const storageFile = fileMap[fileId];

          return {
            ...doc,
            id: fileId || doc.id,
            fileId: fileId || doc.fileId,
            name:
              storageFile?.name ||
              storageFile?.filename ||
              doc.name ||
              "Documento",
            url: storageFile?.url || doc.url || null,
            hash: doc.hash ?? storageFile?.hash ?? null,
          };
        });

        setDocuments(merged);
      } catch (e) {
        console.error("Error loading existing documents for commitment:", e);
        setDocuments(compromisoEditing?.documents ?? []);
      }
    };

    loadExistingDocuments();
  }, [isEditing, compromisoEditing]);

  const initialState = {
    _socioType: Web3Manager.MEMBER_DATA.memberType,
    _socioCode: Web3Manager.MEMBER_DATA.memberCode,
    _commitmentCode: compromiso.code,
    _status: Constants.HAS_INTENTION,
    _deadlineDate: moment().add(3, "months").format("YYYY-MM-DD"),
    _modificationDate: moment().format("YYYY-MM-DD"),
  };

  const getFormData = () => {
    let formData;

    if (!isEditing) {
      formData = initialState;
    } else {
      formData = {
        _socioType: Web3Manager.MEMBER_DATA.memberType,
        _socioCode: Web3Manager.MEMBER_DATA.memberCode,
        _commitmentCode: compromiso.code,
        _status: compromisoEditing.status,
        _deadlineDate:
          compromisoEditing.status === Constants.HAS_INTENTION
            ? moment(compromisoEditing.deadlineDate).format("YYYY-MM-DD")
            : moment().add(3, "months").format("YYYY-MM-DD"),
        _modificationDate: moment(compromisoEditing.modificationDate).format(
          "YYYY-MM-DD"
        ),
      };
    }

    return formData;
  };

  const formAdherirCompromiso = useFormik({
    initialValues: getFormData(),
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      const {
        _socioType,
        _socioCode,
        _commitmentCode,
        _status,
        _deadlineDate,
        _modificationDate,
      } = values;

      let _deadlineDateParsed = moment(_deadlineDate, "YYYY-MM-DD").unix();
      const _modificationDateParsed = moment(
        _modificationDate,
        "YYYY-MM-DD"
      ).unix();

      if (_status === Constants.COMPLIES) {
        _deadlineDateParsed = 0;
      }

      const commitmentDocuments = documents
      .map((d) => {
        const fileId = d.fileId ?? d.id;
        if (!fileId) return null;
    
        return {
          hash: d?.hash ?? null,
          fileId,
        };
      })
      .filter(Boolean);
      setDisableButton(true);
      openSpinner();

      try {
        if (!isEditing) {
          const response = await adherirACompromiso(
            _socioType,
            _socioCode,
            _commitmentCode,
            _status,
            _deadlineDateParsed,
            _modificationDateParsed,
            commitmentDocuments
          );
          setDisableButton(false);
          resetForm();
          handleClose(false, response, true);
        } else {
          if (_status === Constants.REMOVE_COMMITMENT) {
            const response = await eliminarAdhesionCompromiso(
              _socioCode,
              _socioType,
              _commitmentCode
            );
            setDisableButton(false);
            resetForm();
            handleClose(false, response, true);
          } else {
            const response = await editarAdhesionCompromiso(
              _socioType,
              _socioCode,
              _commitmentCode,
              _status,
              _deadlineDateParsed,
              _modificationDateParsed,
              commitmentDocuments
            );
            setDisableButton(false);
            resetForm();
            handleClose(false, response, true);
          }
        }
      } catch (error) {
        setDisableButton(false);
        handleClose(false, null, false);
      } finally {
        closeSpinner();
      }
    },
  });

  const { handleSubmit, handleChange, handleBlur, values, resetForm } =
    formAdherirCompromiso;

  const onUploadDocumentsSuccess = (newDocs = []) => {
    const mapped = newDocs.map((d) => ({
      ...d,
      id: d.id,
      fileId: d.id,
      name: d.filename || d.name,
    }));
    setDocuments((prev) => [...prev, ...mapped]);
  };

  const handleDeleteDoc = async (docId) => {
    try {
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        resetForm();
        handleClose(true, null, false);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box container sx={style}>
        <Grid container direction="column" spacing={2} sx={{ padding: 3 }}>
          <Typography variant="h6">
            {!isEditing
              ? `Adherir al compromiso ${compromiso.title}`
              : `Editar adhesión al compromiso ${compromiso.title}`}
          </Typography>

          <Grid container spacing={2} mt={1}>
            <Grid item>
              <TextField
                select
                name="_status"
                label="Estado del compromiso"
                value={values._status}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                size="small"
                variant="outlined"
                sx={{ width: 200 }}
              >
                <MenuItem value={Constants.COMPLIES}>Cumple</MenuItem>
                <MenuItem value={Constants.HAS_INTENTION}>
                  Tiene intención
                </MenuItem>
                {isEditing && (
                  <MenuItem value={Constants.REMOVE_COMMITMENT}>
                    Eliminar adhesión
                  </MenuItem>
                )}
              </TextField>
            </Grid>

            <Grid item>
              {values._status === Constants.HAS_INTENTION && (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Fecha límite"
                    inputFormat="DD-MM-YYYY"
                    value={values._deadlineDate}
                    onChange={(newValue) => {
                      formAdherirCompromiso.setFieldValue(
                        "_deadlineDate",
                        moment(newValue).format("YYYY-MM-DD")
                      );
                    }}
                    disablePast
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{ width: 200 }}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            </Grid>
          </Grid>

          {!isEditing && (
            <Box mt={3} mb={2}>
              <Alert severity="info" sx={{ fontSize: "12px" }}>
                <AlertTitle>
                  ¿Está seguro que desea adherirse al compromiso?
                </AlertTitle>
              </Alert>
            </Box>
          )}

          {/* Documentos */}
          <Box mt={3} mb={2}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Documentos
              </Typography>
              <UploadDocumentsModal
                openButton={
                  <Button variant="contained" sx={{ textTransform: "none" }}>
                    Agregar documento
                  </Button>
                }
                onSave={onUploadDocumentsSuccess}
              />
            </Stack>

            {documents.length > 0 ? (
              <Stack mt={2} gap={1}>
                {documents.map((doc, index) => (
                  <Stack
                    key={doc.fileId ?? doc.id ?? doc.url ?? index}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      p: 1,
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography fontWeight={500}>
                        {doc.name || doc.fileName || "Documento"}
                      </Typography>

                      {doc.hash && (
                        <Tooltip
                          title={
                            copiedIndex === index
                              ? "¡Copiado!"
                              : `SHA-256: ${doc.hash}`
                          }
                        >
                          <Chip
                            size="small"
                            label={
                              copiedIndex === index
                                ? "¡Copiado!"
                                : `SHA-256: ${shortHash(doc.hash)}`
                            }
                            variant="outlined"
                            onClick={() =>
                              copyHash(doc.hash, setCopiedIndex, index)
                            }
                            sx={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      )}

                      {doc.txHash && (
                        <Tooltip title="Verificado en blockchain">
                          <Verified fontSize="small" color="success" />
                        </Tooltip>
                      )}
                    </Stack>

                    <Stack direction="row" gap={1}>
                      {doc.url && (
                        <Tooltip title="Descargar">
                          <IconButton
                            component={Link}
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                          >
                            <CloudDownload fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteDoc(doc.id)}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography mt={1} color="text.secondary">
                Aún no hay documentos adjuntos
              </Typography>
            )}
          </Box>

          <Box
            mt={2}
            sx={{ justifyContent: "right", display: "flex", gap: "10px" }}
          >
            <Button
              variant="contained"
              color="primary"
              type="submit"
              endIcon={<CheckCircle />}
              onClick={handleSubmit}
              disabled={isLoading || disableButton}
            >
              Confirmar
            </Button>
            <CancelButton
              label="Cancelar"
              onClick={() => {
                resetForm();
                handleClose(true, null, false);
              }}
              disabled={isLoading}
            />
          </Box>
        </Grid>
      </Box>
    </Modal>
  );
};

export default ModalAdherirCompromiso;
